import type { CSSProperties, ReactElement } from 'react';

// Clean line icons on a 24px grid, stroke 1.7, round caps — ported verbatim
// from the redesign prototype (tickets/icons.jsx) for pixel fidelity.

const S = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};
const F = { fill: 'currentColor' };

const PATHS = {
  back: <path d="M15 5l-7 7 7 7" {...S} />,
  chevronRight: <path d="M9 5l7 7-7 7" {...S} />,
  chevronDown: <path d="M6 9l6 6 6-6" {...S} />,
  chevronUp: <path d="M6 15l6-6 6 6" {...S} />,
  close: <path d="M6 6l12 12M18 6L6 18" {...S} />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" {...S} />,
  search: (
    <g {...S}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M20 20l-3.8-3.8" />
    </g>
  ),
  swap: (
    <g {...S}>
      <path d="M7 4v15M7 4L4 7.4M7 4l3 3.4" />
      <path d="M17 20V5M17 20l3-3.4M17 20l-3-3.4" />
    </g>
  ),
  plus: <path d="M12 5v14M5 12h14" {...S} />,
  minus: <path d="M5 12h14" {...S} />,
  arrowRight: <path d="M5 12h14M13 6l6 6-6 6" {...S} />,
  metro: (
    <g {...S}>
      <rect x="6" y="4" width="12" height="12.5" rx="3.5" />
      <rect x="8.4" y="6.6" width="7.2" height="3.4" rx="1.1" />
      <circle cx="9.2" cy="13.2" r="0.7" {...F} stroke="none" />
      <circle cx="14.8" cy="13.2" r="0.7" {...F} stroke="none" />
      <path d="M8.6 16.5L7 19.4M15.4 16.5L17 19.4" />
      <path d="M5 19.4h14" />
    </g>
  ),
  bus: (
    <g {...S}>
      <rect x="4" y="4" width="16" height="12.5" rx="3.2" />
      <rect x="6.2" y="6.5" width="11.6" height="3.6" rx="1.1" />
      <circle cx="8" cy="13.6" r="0.65" {...F} stroke="none" />
      <circle cx="16" cy="13.6" r="0.65" {...F} stroke="none" />
      <path d="M7.5 16.5v1.8M16.5 16.5v1.8" />
    </g>
  ),
  auto: (
    <g {...S}>
      <path d="M4 14.2v-2.4C4 8.1 6.6 5.6 10 5.6h1.3c1.6 0 3.1 1 3.8 2.5l1.9 4.1" />
      <path d="M3.3 14.2h14.4" />
      <path d="M10.4 5.7V14.2" />
      <path d="M5.7 11.4h4.4" />
      <circle cx="7" cy="16.4" r="1.9" />
      <circle cx="14.9" cy="16.4" r="1.9" />
    </g>
  ),
  cab: (
    <g {...S}>
      <path d="M3.4 14v-1.5c0-.6.4-1.1 1-1.3l1.8-.5 2-2.6c.5-.6 1.2-1 2-1h3.6c.8 0 1.5.4 2 1l2 2.6 1.8.5c.6.2 1 .7 1 1.3V14" />
      <path d="M3 14h18" />
      <circle cx="7.3" cy="16" r="1.9" />
      <circle cx="16.3" cy="16" r="1.9" />
      <rect x="9.6" y="4" width="4.8" height="2.3" rx="0.7" />
      <path d="M8.6 8l.5 2.6h5.8l-.5-2.6" />
    </g>
  ),
  ticket: (
    <g {...S}>
      <path d="M3.5 9A1.8 1.8 0 015.3 7.2h13.4A1.8 1.8 0 0120.5 9v1.4a1.7 1.7 0 000 3.2V15a1.8 1.8 0 01-1.8 1.8H5.3A1.8 1.8 0 013.5 15v-1.4a1.7 1.7 0 000-3.2z" />
      <path d="M14.5 7.6v8.8" strokeDasharray="1.3 2.1" />
    </g>
  ),
  pin: (
    <g {...S}>
      <path d="M12 21c4-4.5 6.5-7.8 6.5-11a6.5 6.5 0 10-13 0c0 3.2 2.5 6.5 6.5 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </g>
  ),
  location: (
    <g {...S}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v3M12 19v3M22 12h-3M5 12H2" />
    </g>
  ),
  qr: (
    <g {...S}>
      <rect x="4" y="4" width="6" height="6" rx="1.4" />
      <rect x="14" y="4" width="6" height="6" rx="1.4" />
      <rect x="4" y="14" width="6" height="6" rx="1.4" />
      <path d="M14 14h2v2M20 14v6M14 20h2M18 18v2" />
    </g>
  ),
  refresh: (
    <g {...S}>
      <path d="M20 11a8 8 0 10-1.5 5.5" />
      <path d="M20 4v5h-5" />
    </g>
  ),
  clock: (
    <g {...S}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 7.5V12l3 2" />
    </g>
  ),
  calendar: (
    <g {...S}>
      <rect x="4" y="5.5" width="16" height="15" rx="3" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </g>
  ),
  phone: (
    <path
      d="M6.5 4h3l1.5 4-2 1.5a11 11 0 005 5l1.5-2 4 1.5v3a2 2 0 01-2.2 2A16 16 0 014.5 6.2 2 2 0 016.5 4z"
      {...S}
    />
  ),
  user: (
    <g {...S}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5.5 20a6.5 6.5 0 0113 0" />
    </g>
  ),
  home: (
    <g {...S}>
      <path d="M4 11l8-6.5 8 6.5" />
      <path d="M6 9.5V20h12V9.5" />
    </g>
  ),
  trips: (
    <g {...S}>
      <path d="M5 4.5h14M5 4.5v15l3.5-2 3.5 2 3.5-2 3.5 2v-15" />
      <path d="M8.5 9h7M8.5 13h5" />
    </g>
  ),
  support: (
    <g {...S}>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="3.2" />
      <path d="M14.3 9.7l3.1-3.1M6.6 17.4l3.1-3.1M14.3 14.3l3.1 3.1M6.6 6.6l3.1 3.1" />
    </g>
  ),
  bell: (
    <g {...S}>
      <path d="M6 10a6 6 0 1112 0c0 4 1.5 5.5 1.5 5.5H4.5S6 14 6 10z" />
      <path d="M10 19a2 2 0 004 0" />
    </g>
  ),
  star: (
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z" {...S} />
  ),
  starFill: (
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9z" {...F} />
  ),
  shield: (
    <g {...S}>
      <path d="M12 3l7 2.5v5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5v-5L12 3z" />
      <path d="M9 12l2 2 4-4" />
    </g>
  ),
  alert: (
    <g {...S}>
      <path d="M12 4l8.5 15h-17L12 4z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="16.6" r="0.5" {...F} stroke="none" />
    </g>
  ),
  info: (
    <g {...S}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M12 11v5" />
      <circle cx="12" cy="8" r="0.6" {...F} stroke="none" />
    </g>
  ),
  sparkle: (
    <path
      d="M12 3.5c.6 3.9 1.6 4.9 5.5 5.5-3.9.6-4.9 1.6-5.5 5.5-.6-3.9-1.6-4.9-5.5-5.5 3.9-.6 4.9-1.6 5.5-5.5z"
      {...S}
    />
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" {...S} />,
  logout: (
    <g {...S}>
      <path d="M14 5.5H6.5A1.5 1.5 0 005 7v10a1.5 1.5 0 001.5 1.5H14" />
      <path d="M11 12h9M17 8.5l3.5 3.5L17 15.5" />
    </g>
  ),
  female: (
    <g {...S}>
      <circle cx="12" cy="8.5" r="4.5" />
      <path d="M12 13v7M9 17.5h6" />
    </g>
  ),
  wallet: (
    <g {...S}>
      <rect x="3.5" y="6" width="17" height="13" rx="3" />
      <path d="M3.5 10h17" />
      <circle cx="16.5" cy="14.5" r="1.1" {...F} stroke="none" />
    </g>
  ),
  image: (
    <g {...S}>
      <rect x="4" y="5" width="16" height="14" rx="3" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="M5 17l4.5-4 3 2.5 3-3L20 16" />
    </g>
  ),
  more: (
    <g {...F}>
      <circle cx="6" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="18" cy="12" r="1.6" />
    </g>
  ),
  thumbUp: (
    <g {...S}>
      <path d="M7 10v9H5a1 1 0 01-1-1v-7a1 1 0 011-1h2z" />
      <path d="M7 10l3.5-6.2A1.6 1.6 0 0113.4 5v3.5H18a2 2 0 011.95 2.45l-1.3 6A2 2 0 0116.7 19H7" />
    </g>
  ),
  thumbDown: (
    <g {...S}>
      <path d="M17 14V5h2a1 1 0 011 1v7a1 1 0 01-1 1h-2z" />
      <path d="M17 14l-3.5 6.2A1.6 1.6 0 0110.6 19v-3.5H6a2 2 0 01-1.95-2.45l1.3-6A2 2 0 017.3 5H17" />
    </g>
  ),
  escalate: (
    <g {...S}>
      <path d="M12 20V6M12 6l-5 5M12 6l5 5" />
    </g>
  ),
  message: (
    <g {...S}>
      <path d="M5 5.5h14a1.5 1.5 0 011.5 1.5v8a1.5 1.5 0 01-1.5 1.5H9l-4 3.5V16.5H5A1.5 1.5 0 013.5 15V7A1.5 1.5 0 015 5.5z" />
    </g>
  ),
  edit: (
    <g {...S}>
      <path d="M4 20h4L18.5 9.5a2 2 0 00-2.8-2.8L5 17.2 4 20z" />
    </g>
  ),
  checkCircle: (
    <g {...S}>
      <circle cx="12" cy="12" r="8.2" />
      <path d="M8.5 12.2l2.3 2.3 4.7-4.7" />
    </g>
  ),
  dot: <circle cx="12" cy="12" r="4" {...F} />,
  walk: (
    <g {...S}>
      <circle cx="13" cy="4.6" r="1.8" />
      <path d="M11 9l3-1 2 3 3 1M11 9l-1.5 4 2 1.5V21M9.5 13L7 21M13 15.5l1 1.5" />
    </g>
  ),
  car: (
    <g {...S}>
      <path d="M4 16v-3l1.8-4.5C6.1 7.6 6.9 7 7.8 7h8.4c.9 0 1.7.6 2 1.5L20 13v3" />
      <path d="M3.5 16h17" />
      <circle cx="7.5" cy="17.5" r="1.8" />
      <circle cx="16.5" cy="17.5" r="1.8" />
    </g>
  ),
  tag: (
    <g {...S}>
      <path d="M4 11.5V5.5A1.5 1.5 0 015.5 4h6a2 2 0 011.4.6l6 6a2 2 0 010 2.8l-5 5a2 2 0 01-2.8 0l-6-6a2 2 0 01-.6-1.4z" />
      <circle cx="8.5" cy="8.5" r="1.2" {...F} stroke="none" />
    </g>
  ),
  gift: (
    <g {...S}>
      <rect x="4" y="9" width="16" height="4" rx="1" />
      <path d="M5.5 13v6.5a1 1 0 001 1h11a1 1 0 001-1V13M12 9v11.5" />
      <path d="M12 9S10.5 4.5 8 5.5 10 9 12 9zM12 9s1.5-4.5 4-3.5S14 9 12 9z" />
    </g>
  ),
} satisfies Record<string, ReactElement>;

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 22,
  style,
  className,
  strokeWidth,
}: {
  name: IconName;
  size?: number;
  style?: CSSProperties;
  className?: string;
  strokeWidth?: number;
}) {
  const inner = PATHS[name];
  if (!inner) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
    >
      {strokeWidth ? <g strokeWidth={strokeWidth}>{inner}</g> : inner}
    </svg>
  );
}
